import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQuery } from '@tanstack/react-query';
import { classNames } from 'primereact/utils';
import { Button } from 'primereact/button';
import { Calendar } from 'primereact/calendar';
import { Dropdown } from 'primereact/dropdown';
import { FileUpload } from 'primereact/fileupload';
import { InputText } from 'primereact/inputtext';
import { Controller, useForm } from 'react-hook-form';
import { z } from 'zod';
import { endpointJurisdictionsGET } from '~/api/def/endpoints/jurisdictionEndpoint';
import {
  COMPANY_STATUS,
  portfolioCompanyPutPayloadSchema,
} from '~/api/def/endpoints/portfolioCompanyEndpoint';
import { endpointSectorGET } from '~/api/def/endpoints/sectorEndpoint';
import { FormLabelInGrid } from '~/components/forms/FormLabelInGrid';
import { BqFormErrorText } from '~/components/ui/misc/bqTag/BqFormErrorText';

import { format } from 'date-fns';
import { Dialog } from 'primereact/dialog';
import { useEffect, useState } from 'react';
import { GoogleDriveUploader } from '~/components/ui/uploaders/GoogleDriveUploader';
import { uploadLogo } from '../utils/uploadLogo';

export const date2ApiString = (date: Date) => {
  return format(date, 'yyyy-MM-dd');
};

export type EditPortfolioCompanyValues = z.infer<
  typeof portfolioCompanyPutPayloadSchema
>;

export type EditPortfolioCompanyProps = {
  initialValue: EditPortfolioCompanyValues;
  id: string;
  submitText?: string;
  onSubmit: (data: EditPortfolioCompanyValues) => Promise<void> | void;
  isLoading: boolean;
  onLogoUploadError: (error: unknown) => void;
  fundId: string;
  refetchPcDate: () => void;
};

export function EditPortfolioCompany({
  initialValue,
  id,
  onSubmit,
  isLoading,
  onLogoUploadError,
  fundId,
  refetchPcDate,
}: EditPortfolioCompanyProps) {
  const [gDriveFolderSyncVisible, setGDriveFolderSyncVisible] = useState(false);
  const { data: sectorsData, isLoading: isSectorsLoading } = useQuery({
    queryKey: ['portfolioSectorsGet'],
    queryFn: endpointSectorGET,
  });

  const { data: jurisdictionsData, isLoading: isJurisdictionsLoading } =
    useQuery({
      queryKey: ['jurisdictionsGet'],
      queryFn: endpointJurisdictionsGET,
    });

  const logoMutation = useMutation({
    mutationFn: async (
      data: EditPortfolioCompanyValues & { logoFile: File | string },
    ) => {
      const logoFileKey =
        data.logoFile instanceof File
          ? await uploadLogo(data.logoFile, id)
          : data.logoFile;

      return { ...data, logo: logoFileKey };
    },
    onSuccess: onSubmit,
    onError: onLogoUploadError,
  });

  const { control, handleSubmit, reset } = useForm<
    EditPortfolioCompanyValues & { logoFile: File | string }
  >({
    defaultValues: { ...initialValue, logoFile: '' },
    resolver: zodResolver(portfolioCompanyPutPayloadSchema.passthrough()),
  });

  useEffect(() => {
    reset({ ...initialValue, logoFile: '' });
  }, [initialValue, reset]);

  const handleFormSubmit = async (
    data: EditPortfolioCompanyValues & { logoFile: File | string },
  ) => {
    await logoMutation.mutateAsync(data);
  };

  return (
    <div className="card flex justify-content-center">
      <Dialog
        header="Select folder for Auto Sync"
        className="pb-0"
        visible={gDriveFolderSyncVisible}
        style={{ width: '50vw' }}
        onHide={() => setGDriveFolderSyncVisible(false)}
      >
        <GoogleDriveUploader
          onSubmitComplete={() => {
            refetchPcDate();
            setGDriveFolderSyncVisible(false);
          }}
          isGDriveSyncMode
          portfolioCompanyId={id}
          fundId={fundId}
        />
      </Dialog>
      <form
        onSubmit={handleSubmit(handleFormSubmit)}
        className="flex flex-column gap-2"
      >
        <div className="flex-column flex h-full">
          <div className="grid">
            <Controller
              name="name"
              control={control}
              rules={{ required: 'Company Name is required.' }}
              render={({ field, fieldState }) => (
                <>
                  <FormLabelInGrid
                    required
                    htmlFor={field.name}
                    label="Company Name"
                  />
                  <div className="col col-9 text-left">
                    <InputText
                      id={field.name}
                      className={classNames({
                        'p-invalid': fieldState.error,
                        'w-16rem': true,
                      })}
                      value={field.value}
                      onChange={(e) => field.onChange(e.target.value)}
                    />
                    <BqFormErrorText error={fieldState.error?.message} />
                  </div>
                </>
              )}
            />

            <Controller
              name="shortName"
              control={control}
              render={({ field, fieldState }) => (
                <>
                  <FormLabelInGrid htmlFor={field.name} label="Short Name" />
                  <div className="col col-9 text-left">
                    <InputText
                      id={field.name}
                      className={classNames({
                        'p-invalid': fieldState.error,
                        'w-16rem': true,
                      })}
                      value={field.value}
                      onChange={(e) => field.onChange(e.target.value)}
                    />
                    <BqFormErrorText error={fieldState.error?.message} />
                  </div>
                </>
              )}
            />

            <Controller
              name="currentRound"
              control={control}
              rules={{ required: 'Current Round is required.' }}
              render={({ field, fieldState }) => (
                <>
                  <FormLabelInGrid
                    required
                    htmlFor={field.name}
                    label="Current Round"
                  />
                  <div className="col col-9 text-left">
                    <InputText
                      id={field.name}
                      className={classNames({
                        'p-invalid': fieldState.error,
                        'w-16rem': true,
                      })}
                      value={field.value}
                      onChange={(e) => field.onChange(e.target.value)}
                    />
                    <BqFormErrorText error={fieldState.error?.message} />
                  </div>
                </>
              )}
            />

            <Controller
              name="companyStatus"
              control={control}
              rules={{ required: 'Company Status is required.' }}
              render={({ field, fieldState }) => (
                <>
                  <FormLabelInGrid
                    required
                    htmlFor={field.name}
                    label="Company Status"
                  />
                  <div className="col col-9 text-left">
                    <Dropdown
                      id={field.name}
                      value={field.value}
                      className={classNames({
                        'p-invalid': fieldState.error,
                        'w-16rem': true,
                      })}
                      options={Object.values(COMPANY_STATUS).map((status) => ({
                        label: status,
                        value: status,
                      }))}
                      onChange={(e) => field.onChange(e.target.value)}
                    />
                    <BqFormErrorText error={fieldState.error?.message} />
                  </div>
                </>
              )}
            />

            <Controller
              name="sectorId"
              control={control}
              rules={{ required: 'Select Sector.' }}
              render={({ field, fieldState }) => (
                <>
                  <FormLabelInGrid
                    required
                    htmlFor={field.name}
                    label="Sector"
                  />
                  <div className="col col-9 text-left">
                    <Dropdown
                      id="sectorId"
                      value={field.value}
                      className="w-16rem"
                      loading={isSectorsLoading}
                      filter
                      options={sectorsData?.map((sector) => ({
                        label: sector.name,
                        value: sector.id,
                        id: sector.id,
                      }))}
                      onChange={(e) => field.onChange(e.target.value)}
                    />
                    <BqFormErrorText error={fieldState.error?.message} />
                  </div>
                </>
              )}
            />

            <Controller
              name="jurisdictionId"
              control={control}
              render={({ field, fieldState }) => (
                <>
                  <FormLabelInGrid htmlFor={field.name} label="Jurisdiction" />
                  <div className="col col-9 text-left">
                    <Dropdown
                      id="jurisdictionId"
                      value={field.value}
                      className="w-16rem"
                      loading={isJurisdictionsLoading}
                      filter
                      options={jurisdictionsData?.data.map((jurisdiction) => ({
                        label: jurisdiction.name,
                        value: jurisdiction.id,
                        id: jurisdiction.id,
                      }))}
                      onChange={(e) => field.onChange(e.target.value)}
                    />
                    <BqFormErrorText error={fieldState.error?.message} />
                  </div>
                </>
              )}
            />

            <Controller
              name="incorporationDate"
              control={control}
              rules={{ required: 'Select incorporation date.' }}
              render={({ field, fieldState }) => (
                <>
                  <FormLabelInGrid
                    required
                    htmlFor={field.name}
                    label="Incorporation Date"
                  />
                  <div className="col col-9 text-left">
                    <Calendar
                      id={field.name}
                      value={field.value ? new Date(field.value) : undefined}
                      onChange={(e) => {
                        field.onChange(e?.value ? date2ApiString(e.value) : '');
                      }}
                      showIcon
                    />
                    <BqFormErrorText error={fieldState.error?.message} />
                  </div>
                </>
              )}
            />

            <Controller
              name="closeDate"
              control={control}
              render={({ field, fieldState }) => (
                <>
                  <FormLabelInGrid
                    required={false}
                    htmlFor={field.name}
                    label="Close Date"
                  />
                  <div className="col col-9 text-left">
                    <Calendar
                      id={field.name}
                      value={field.value ? new Date(field.value) : undefined}
                      onChange={(e) => {
                        field.onChange(e?.value ? date2ApiString(e.value) : '');
                      }}
                      showIcon
                    />
                    <BqFormErrorText error={fieldState.error?.message} />
                  </div>
                </>
              )}
            />

            <Controller
              name="logoFile"
              control={control}
              render={({ field }) => (
                <>
                  <FormLabelInGrid htmlFor={field.name} label="Company Logo" />
                  <div className="col col-9 text-left">
                    {field.value && !(field.value instanceof File) && (
                      <div className="mb-2">
                        <img
                          src={field.value}
                          alt="Current logo"
                          style={{ maxWidth: '100px' }}
                        />
                      </div>
                    )}
                    {field.value instanceof File && (
                      <div className="mb-2">
                        <img
                          src={URL.createObjectURL(field.value)}
                          alt="Preview"
                          style={{ maxWidth: '100px' }}
                        />
                      </div>
                    )}
                    <div className="flex align-items-center">
                      <FileUpload
                        id={field.name}
                        mode="basic"
                        name="logoFile"
                        accept="image/*"
                        maxFileSize={1000000}
                        chooseLabel="Choose Logo"
                        auto={false}
                        customUpload={true}
                        onSelect={(e) => {
                          if (e.files && e.files.length > 0) {
                            const selectedFile = e.files[0];
                            field.onChange(selectedFile);
                          }
                        }}
                        onRemove={() => field.onChange(null)}
                        onClear={() => field.onChange(null)}
                      />
                      <Button
                        type="button"
                        className="ml-2"
                        onClick={() => field.onChange(null)}
                      >
                        Cancel
                      </Button>
                    </div>
                  </div>
                </>
              )}
            />
            <Controller
              name="gDriveFolder"
              control={control}
              render={({ field, fieldState }) => (
                <>
                  <FormLabelInGrid
                    required={false}
                    htmlFor={field.name}
                    label="Google Drive Folder"
                  />
                  <div className="col col-2 text-left">
                    <InputText
                      id={field.name}
                      className={classNames({
                        'p-invalid': fieldState.error,
                        'w-16rem': true,
                      })}
                      value={field.value}
                      onChange={(e) => field.onChange(e.target.value)}
                    />
                    <BqFormErrorText error={fieldState.error?.message} />
                  </div>
                  <div className="col-6 flex align-items-center">
                    <Button
                      type="button"
                      className="ml-2"
                      onClick={() => setGDriveFolderSyncVisible(true)}
                    >
                      Update Folder
                    </Button>
                  </div>
                </>
              )}
            />

            <div className="col col-3 text-right pt-3"></div>
            <div className="col col-9 text-left">
              <Button
                loading={isLoading || logoMutation.isPending}
                disabled={isLoading || logoMutation.isPending}
              >
                Save
              </Button>
            </div>
          </div>
        </div>
      </form>
    </div>
  );
}
